import { useMutation, useQuery } from '@tanstack/react-query';
import {
  archiveSlcpAssessments,
  createSlcpAssessmentHistory,
  fetchAssessmentAttachmentLink,
  fetchAssessmentById,
  fetchMySlcpAssessments,
  fetchProfileDataPoints,
  triggerGatewayAssessmentFetch,
  unarchiveSlcpAssessments,
} from '@/api/slcp';
import type {
  AssessmentAttachmentResponse,
  MySlcpAssessmentsParams,
  SlcpAssessmentDetail,
  SlcpDataPointDef,
} from '@/api/slcp/types';

export const mySlcpAssessmentsKeys = {
  all: ['slcp', 'my-assessments'] as const,
  list: (isArchived: boolean) =>
    [...mySlcpAssessmentsKeys.all, { isArchived }] as const,
};

function buildMySlcpAssessmentsQs(params: MySlcpAssessmentsParams): string {
  const qs = new URLSearchParams();
  qs.set('filter', JSON.stringify({ isArchived: params.isArchived }));
  if (params.companyProfile) qs.set('companyProfile', params.companyProfile);
  return qs.toString();
}

export function useMySlcpAssessmentsQuery(
  isArchived: boolean,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: mySlcpAssessmentsKeys.list(isArchived),
    queryFn: () =>
      fetchMySlcpAssessments(buildMySlcpAssessmentsQs({ isArchived })),
    staleTime: 30_000,
    enabled: options.enabled ?? true,
    retry: false,
  });
}

export { buildMySlcpAssessmentsQs };

export function useArchiveSlcpAssessments() {
  return useMutation({
    mutationFn: (ids: string[]) => archiveSlcpAssessments(ids),
  });
}

export function useUnarchiveSlcpAssessments() {
  return useMutation({
    mutationFn: (ids: string[]) => unarchiveSlcpAssessments(ids),
  });
}

export function useTriggerGatewaySync() {
  return useMutation({ mutationFn: triggerGatewayAssessmentFetch });
}

export function useFetchAttachmentLink() {
  return useMutation<AssessmentAttachmentResponse, Error, string>({
    // `GET /api/slcp/assessments/:id/attachment` answers `{ metadata, data }`.
    mutationFn: async assessmentId =>
      (await fetchAssessmentAttachmentLink(assessmentId)).data,
  });
}

export function useCreateSlcpAssessmentHistory() {
  return useMutation({
    mutationFn: (assessmentId: string) =>
      createSlcpAssessmentHistory({ assessmentId }),
  });
}

export function useAssessmentDetailQuery(
  assessmentId: string | null,
  enabled: boolean,
) {
  return useQuery<SlcpAssessmentDetail>({
    queryKey: ['slcp', 'assessment-detail', assessmentId],
    queryFn: () => fetchAssessmentById(assessmentId as string),
    enabled: enabled && !!assessmentId,
    staleTime: 60_000,
  });
}

export function useProfileDataPointsQuery(enabled: boolean) {
  return useQuery<SlcpDataPointDef[]>({
    queryKey: ['slcp', 'profile-data-points'],
    queryFn: () => fetchProfileDataPoints(),
    enabled,
    staleTime: 5 * 60_000,
  });
}
